import { useState, useRef, useCallback } from 'react';
import { useIssueStore, useProjectStore } from '@/stores';
import type { IssueListType, ImportPreview, ImportIssueItem, CreateIssueDTO } from '@/types';
import { ISSUE_TYPE_LABELS } from '@/types';
import { Button } from '@/components/ui';
import { X, Upload, FileText, CheckCircle, AlertCircle, Loader2, AlertCircle as ImportIcon } from 'lucide-react';

interface IssueImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  issueType: IssueListType;
}

export function IssueImportModal({ isOpen, onClose, issueType }: IssueImportModalProps) {
  const { currentProject } = useProjectStore();
  const { importIssues, confirmImport, isLoading } = useIssueStore();
  
  const [step, setStep] = useState<'upload' | 'preview' | 'success'>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [selectedIssues, setSelectedIssues] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = ['.xlsx', '.xls', '.csv'];
      const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
      
      if (validTypes.includes(fileExt)) {
        setSelectedFile(file);
        setError(null);
      } else {
        setError('不支持的文件格式，请上传 .xlsx, .xls 或 .csv 文件');
      }
    }
  }, []);

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('请先选择要上传的文件');
      return;
    }
    
    if (!currentProject) {
      setError('请先选择一个项目，然后再导入问题');
      return;
    }
    
    setError(null);
    try {
      const result = await importIssues(selectedFile, issueType, currentProject.id);
      setPreview(result);
      setSelectedIssues(new Set(result.issues.map((_, i) => i)));
      setStep('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : '导入失败，请检查文件格式');
    }
  };

  const toggleIssue = (index: number) => {
    const newSelected = new Set(selectedIssues);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedIssues(newSelected);
  };

  const toggleAll = () => {
    if (!preview) return;
    if (selectedIssues.size === preview.issues.length) {
      setSelectedIssues(new Set());
    } else {
      setSelectedIssues(new Set(preview.issues.map((_, i) => i)));
    }
  };

  const handleConfirm = async () => {
    if (!preview) return;
    
    if (!currentProject?.id) {
      setError('请先选择一个项目，然后再导入问题');
      return;
    }
    
    const parseActions = (actionText: string): string[] => {
      if (!actionText) return [];
      return actionText
        .split(/\n+/)
        .map(s => s.trim())
        .filter(s => s.length > 0);
    };
    
    const issuesToImport: CreateIssueDTO[] = preview.issues
      .filter((_, i) => selectedIssues.has(i))
      .map((item) => {
        const actionItems: CreateActionItemDTO[] = [];
        
        const shortTermActions = parseActions(item.shortTermAction);
        shortTermActions.forEach((content, idx) => {
          actionItems.push({
            issueId: '',
            actionType: 'short_term' as const,
            content,
            breakpointDate: item.stBreakpoint && item.stBreakpoint !== 'Unknown' ? item.stBreakpoint : undefined,
          });
        });
        
        const longTermActions = parseActions(item.longTermAction);
        longTermActions.forEach((content, idx) => {
          actionItems.push({
            issueId: '',
            actionType: 'long_term' as const,
            content,
            breakpointDate: item.ltBreakpoint && item.ltBreakpoint !== 'Unknown' ? item.ltBreakpoint : undefined,
          });
        });
        
        return {
          projectId: currentProject?.id || '',
          issueType,
          description: item.description,
          type: item.type,
          status: item.status || '0/4',
          openDate: item.openDate || new Date().toISOString().split('T')[0],
          rootCause: item.rootCause,
          owner: item.owner,
          station: issueType === 'production' ? item.station : undefined,
          actionItems: actionItems.length > 0 ? actionItems : undefined,
        };
      });

    try {
      await confirmImport(issuesToImport);
      setStep('success');
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : '确认导入失败');
    }
  };

  const handleClose = () => {
    setStep('upload');
    setSelectedFile(null);
    setPreview(null);
    setSelectedIssues(new Set());
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-white px-4 py-3 flex items-center justify-between border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <ImportIcon className="w-5 h-5 text-gray-500" />
            <h3 className="text-lg font-bold text-gray-800">
              Import {issueType === 'product' ? 'Product' : 'Production'} Issues
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-2">
          {step === 'upload' && (
            <div className="space-y-4 mt-2">
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${
                  isDragging 
                    ? 'border-blue-500 bg-blue-100' 
                    : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                }`}
              >
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600 mb-2">点击选择文件或拖拽文件到此处</p>
                <p className="text-sm text-gray-400">支持 .xlsx, .xls, .csv 格式</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {selectedFile && (
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <FileText className="w-8 h-8 text-blue-500" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">{selectedFile.name}</p>
                    <p className="text-sm text-gray-500">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 p-4 bg-red-50 text-red-600 rounded-lg">
                  <AlertCircle className="w-5 h-5" />
                  <span>{error}</span>
                </div>
              )}
            </div>
          )}

          {step === 'preview' && preview && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">
                    共解析 {preview.totalCount} 条问题
                  </span>
                  <span className="text-sm text-gray-400">
                    (已选择 {selectedIssues.size} 条)
                  </span>
                </div>
                <Button variant="ghost" size="sm" onClick={toggleAll}>
                  {selectedIssues.size === preview.issues.length ? '取消全选' : '全选'}
                </Button>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-80 overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="w-12 px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedIssues.size === preview.issues.length}
                            onChange={toggleAll}
                            className="rounded"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          问题描述
                        </th>
                        <th className="w-24 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          类型
                        </th>
                        <th className="w-20 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          置信度
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {preview.issues.map((issue, index) => (
                        <tr
                          key={index}
                          onClick={() => toggleIssue(index)}
                          className={`cursor-pointer hover:bg-gray-50 ${
                            selectedIssues.has(index) ? 'bg-blue-50' : ''
                          }`}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedIssues.has(index)}
                              onChange={() => toggleIssue(index)}
                              className="rounded"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-800 line-clamp-2">
                              {issue.description}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-600">
                              {ISSUE_TYPE_LABELS[issue.type]}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${
                                    issue.confidence >= 0.8
                                      ? 'bg-green-500'
                                      : issue.confidence >= 0.5
                                      ? 'bg-yellow-500'
                                      : 'bg-red-500'
                                  }`}
                                  style={{ width: `${issue.confidence * 100}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-500">
                                {Math.round(issue.confidence * 100)}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-4 bg-red-50 text-red-600 rounded-lg">
                  <AlertCircle className="w-5 h-5" />
                  <span>{error}</span>
                </div>
              )}
            </div>
          )}

          {step === 'success' && (
            <div className="flex flex-col items-center justify-center py-12">
              <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">导入成功</h3>
              <p className="text-gray-600">
                已成功导入 {selectedIssues.size} 条问题
              </p>
            </div>
          )}
        </div>

        <div className="bg-white px-4 py-3 flex justify-end border-t border-gray-200 flex-shrink-0">
          {step === 'upload' && (
            <button
              onClick={handleUpload}
              disabled={!selectedFile || !currentProject || isLoading}
              className="group flex items-center bg-transparent border-none cursor-pointer disabled:opacity-50"
            >
              <span className="relative pb-1 text-sm font-medium tracking-wider uppercase text-black pr-4">
                Upload & Parse
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-black transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
              </span>
              <svg
                className="w-5 h-4 text-black transform -translate-x-1 group-hover:translate-x-0 transition-all duration-300 flex-shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          )}
          {step === 'preview' && (
            <button
              onClick={handleConfirm}
              disabled={selectedIssues.size === 0 || isLoading}
              className="group flex items-center bg-transparent border-none cursor-pointer disabled:opacity-50"
            >
              <span className="relative pb-1 text-sm font-medium tracking-wider uppercase text-black pr-4">
                Confirm Import ({selectedIssues.size})
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-black transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
              </span>
              <svg
                className="w-5 h-4 text-black transform -translate-x-1 group-hover:translate-x-0 transition-all duration-300 flex-shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
